import { useState } from 'react'
import { ChevronDown, ChevronUp, SlidersHorizontal, Zap } from 'lucide-react'
import { labelCls } from '../styles'
import type { TmRuleConfig } from '../types'

interface TmConfigPanelProps {
  config: TmRuleConfig
  onChange: (config: TmRuleConfig) => void
}

export function TmConfigPanel({ config, onChange }: TmConfigPanelProps) {
  const [open, setOpen] = useState(true)

  function set<K extends keyof TmRuleConfig>(key: K, value: TmRuleConfig[K]) {
    onChange({ ...config, [key]: value })
  }

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">
          <SlidersHorizontal size={12} className="text-[#4f46e5]" />
          Rule Configuration
        </span>
        {open ? <ChevronUp size={12} className="text-[#64748b]" /> : <ChevronDown size={12} className="text-[#64748b]" />}
      </button>

      {open && (
        <div className="p-4 space-y-4">
          <p className="text-[10px] text-emerald-700 flex items-center gap-1.5">
            <Zap size={11} className="shrink-0" /> These thresholds genuinely drive the simulated rule engine below — adjust them and re-run to see the effect.
          </p>

          <div>
            <label className={labelCls}>Reporting threshold — AUD {config.reportingThreshold.toLocaleString()}</label>
            <input
              type="range" min={1000} max={20000} step={500}
              value={config.reportingThreshold}
              onChange={(e) => set('reportingThreshold', Number(e.target.value))}
              className="w-full" style={{ accentColor: '#6366f1' }}
            />
            <p className="text-[10px] text-[#475569] mt-1">Single transactions at or above this amount are flagged for reporting.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Structuring window — {config.structuringWindowHours}h</label>
              <input
                type="range" min={12} max={96} step={12}
                value={config.structuringWindowHours}
                onChange={(e) => set('structuringWindowHours', Number(e.target.value))}
                className="w-full" style={{ accentColor: '#6366f1' }}
              />
            </div>
            <div>
              <label className={labelCls}>Structuring min. count — {config.structuringMinCount}</label>
              <input
                type="range" min={2} max={6} step={1}
                value={config.structuringMinCount}
                onChange={(e) => set('structuringMinCount', Number(e.target.value))}
                className="w-full" style={{ accentColor: '#6366f1' }}
              />
            </div>
          </div>
          <p className="text-[10px] text-[#475569] -mt-2">Flags repeated transfers to the same counterparty, each just under the reporting threshold, within this window.</p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Velocity window — {config.velocityWindowHours}h</label>
              <input
                type="range" min={6} max={72} step={6}
                value={config.velocityWindowHours}
                onChange={(e) => set('velocityWindowHours', Number(e.target.value))}
                className="w-full" style={{ accentColor: '#6366f1' }}
              />
            </div>
            <div>
              <label className={labelCls}>Velocity total — AUD {config.velocityThreshold.toLocaleString()}</label>
              <input
                type="range" min={5000} max={50000} step={5000}
                value={config.velocityThreshold}
                onChange={(e) => set('velocityThreshold', Number(e.target.value))}
                className="w-full" style={{ accentColor: '#6366f1' }}
              />
            </div>
          </div>
          <p className="text-[10px] text-[#475569] -mt-2">Flags a burst of 3+ outbound transactions within this window totalling more than the threshold.</p>

          <label className="flex items-center gap-3 px-3 py-2.5 rounded-[9px] cursor-pointer" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
            <input type="checkbox" checked={config.flagNewPayee} onChange={(e) => set('flagNewPayee', e.target.checked)} style={{ accentColor: '#6366f1' }} />
            <span className="text-[11px] text-[#475569]">Flag large transfers to newly added payees</span>
          </label>
        </div>
      )}
    </div>
  )
}
